import type { DefLookup } from "./catalog";
import { hasKnownLoadOperatingCurrent } from "./component-current";
import { dcSideTerminals } from "./component-ports";
import type { DcCircuitTopology } from "./dc-circuit-topology";
import type { DesignDeviceMode } from "./dc-design-currents";
import { reachable, splitV, v } from "./diagnostics/graph";
import { effectiveTerminals } from "./effective";
import type { Cable, SystemNode } from "./types";

/** A known consuming port whose positive island contains only passive copper
 * and other consuming positive ports cannot inject current into its shared
 * return. This proves isolation, not a usable design for the floating branch.
 * Unknown components, sources, reversed/series loads and converter stages do
 * not qualify; their current uncertainty must retain the full rail boundary. */
export function isSourceFreeLoadIsland(
  topology: DcCircuitTopology,
  nodeId: string,
  modes: readonly DesignDeviceMode[],
  nodes: Record<string, SystemNode>,
  cables: readonly Cable[],
  lookup: DefLookup,
): boolean {
  if (topology.unsupported.length) return false;
  const ports = modes.flatMap(mode => mode.ports);
  if (!ports.length || new Set(ports.map(item => item.port.id)).size !== 1 ||
      ports.some(item => item.current.sustainedA < 0 || item.current.peakA < 0)) return false;
  const port = ports[0].port;
  const node = nodes[nodeId];
  const def = node?.kind === "component" ? lookup(node.definitionId, node.customDefinition) : undefined;
  if (def?.category !== "load" || !hasKnownLoadOperatingCurrent(def) || (def.ampOutput ?? 0) > 0 || !port.negative.length) return false;
  const returnRails = new Set(port.negative.map(vertex => topology.railId(vertex)));
  if (returnRails.size !== 1) return false;
  const returnRail = [...returnRails][0];
  // A consuming negative tied to source positive may be a reversed or series
  // circuit. An absent correctly polarized route does not prove it is idle.
  if (topology.sources.some(source => source.positive.some(vertex => topology.railId(vertex) === returnRail))) return false;
  const island = new Set(port.positive.flatMap(vertex => [...reachable(topology.graph, vertex)]));
  if (!island.size || cables.some(cable => topology.invalidCableIds.has(cable.id) &&
    (island.has(v(cable.fromNodeId, cable.fromTerminalId)) || island.has(v(cable.toNodeId, cable.toTerminalId))))) return false;
  for (const vertex of island) {
    const endpoint = splitV(vertex), other = nodes[endpoint.nodeId];
    const otherDef = other?.kind === "component" ? lookup(other.definitionId, other.customDefinition) : undefined;
    if (!otherDef || other?.kind !== "component") return false;
    if (otherDef.category === "distribution") {
      if (otherDef.oneWayDcConnections?.length) return false;
      continue;
    }
    if (otherDef.category !== "load" || !hasKnownLoadOperatingCurrent(otherDef) || (otherDef.ampOutput ?? 0) > 0) return false;
    const terminals = effectiveTerminals(otherDef, other);
    const terminal = terminals.find(t => t.id === endpoint.terminalId);
    if (terminal?.polarity !== "positive" || terminal.direction === "output") return false;
    const negatives = dcSideTerminals(otherDef, "input").filter(t => t.polarity === "negative" || t.polarity === "ground");
    if (!negatives.length || negatives.some(t => topology.railId(v(other.id, t.id)) !== returnRail)) return false;
  }
  return true;
}
